import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersTygerbergPage() {
  const [activeTab, setActiveTab] = useState('overview');

  useSEO({
    title: 'Tygerberg Painters | Professional Painting Services | 18+ Years Experience',
    description: 'Expert painters in Tygerberg offering premium residential and commercial painting services. Specializing in suburban homes, medical facilities, and commercial properties. Free quotes, quality workmanship, and professional service guaranteed.',
    keywords: 'Tygerberg painters, painters Tygerberg, painting services Tygerberg, house painters Tygerberg, residential painters, commercial painters, medical facility painters, hospital painters, professional painters',
    ogImage: 'https://public.readdy.ai/ai/img_res/d49c30b50021d35fe0eb7513dd9bbe93.jpg',
    schema: [
      generateLocalBusinessSchema({
        name: 'Painters Tygerberg',
        description: 'Professional painting services in Tygerberg specializing in residential, commercial, and medical facility properties with over 18 years of experience.',
        address: {
          streetAddress: 'Tygerberg',
          addressLocality: 'Cape Town',
          addressRegion: 'Western Cape',
          postalCode: '7505',
          addressCountry: 'ZA'
        },
        geo: {
          latitude: '-33.8789',
          longitude: '18.6234'
        },
        areaServed: ['Tygerberg', 'Bellville', 'Parow', 'Goodwood', 'Brackenfell', 'Durbanville', 'Edgemead'],
        priceRange: '$$',
        image: 'https://readdy.ai/api/search-image?query=professional%20painting%20company%20team%20working%20on%20residential%20property%20modern%20equipment%20clean%20white%20background%20high%20quality%20professional%20photography&width=800&height=600&seq=tygerberg-business&orientation=landscape'
      }),
      generateServiceSchema({
        name: 'Residential and Commercial Painting Services',
        description: 'Premium painting services for homes, businesses, and medical facilities in Tygerberg',
        provider: 'Painters Tygerberg',
        areaServed: 'Tygerberg, Cape Town',
        serviceType: 'Painting Service'
      })
    ]
  });

  const services = [
    {
      title: 'Residential Painting',
      description: 'Professional painting services for suburban homes with quality finishes and attention to detail.',
      icon: 'ri-home-5-line'
    },
    {
      title: 'Medical Facilities',
      description: 'Specialized painting for hospitals, clinics, and medical centers with hygienic coatings.',
      icon: 'ri-hospital-line'
    },
    {
      title: 'Commercial Properties',
      description: 'Expert painting services for offices, retail spaces, and commercial buildings.',
      icon: 'ri-store-2-line'
    },
    {
      title: 'Interior Painting',
      description: 'Professional interior painting with premium finishes and color consultation services.',
      icon: 'ri-paint-brush-line'
    },
    {
      title: 'Exterior Painting',
      description: 'Durable exterior painting with weather-resistant coatings and expert surface preparation.',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Maintenance Programs',
      description: 'Regular maintenance painting programs for residential and commercial properties.',
      icon: 'ri-tools-line'
    }
  ];

  const reviews = [
    {
      name: 'Dr. Johan Pretorius',
      rating: 5,
      text: 'Excellent service for our medical practice. The team was professional, worked around our schedule, and used appropriate hygienic coatings. Very impressed with the quality and efficiency.',
      date: '2 weeks ago'
    },
    {
      name: 'Maria Botha',
      rating: 5,
      text: 'We hired them for our family home and couldn\'t be happier. Professional team, great attention to detail, and the finish is perfect. Highly recommend for residential painting!',
      date: '1 month ago'
    },
    {
      name: 'Peter van Zyl',
      rating: 5,
      text: 'Best painters in the Tygerberg area! They painted our office building and the results are outstanding. Professional, efficient, and excellent quality work.',
      date: '1 month ago'
    },
    {
      name: 'Susan Jacobs',
      rating: 5,
      text: 'Outstanding workmanship! They painted both interior and exterior of our home and handled everything professionally. Great communication throughout the project.',
      date: '2 months ago'
    },
    {
      name: 'Andrew Smith',
      rating: 5,
      text: 'Excellent service for our retail space. They completed the project on time and within budget. The team is reliable, skilled, and a pleasure to work with.',
      date: '2 months ago'
    },
    {
      name: 'Linda de Villiers',
      rating: 5,
      text: 'Transformed our home beautifully! The painters were respectful, clean, and delivered exceptional quality. Very happy with the results and would use them again.',
      date: '3 months ago'
    }
  ];

  const galleryImages = [
    {
      url: 'https://readdy.ai/api/search-image?query=suburban%20family%20home%20exterior%20freshly%20painted%20white%20modern%20architecture%20clean%20simple%20background%20professional%20photography%20bright%20daylight%20residential%20property&width=600&height=400&seq=tygerberg-gallery-1&orientation=landscape',
      title: 'Suburban Family Home'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=medical%20clinic%20interior%20freshly%20painted%20white%20clean%20hygienic%20environment%20clean%20simple%20background%20professional%20photography%20bright%20lighting&width=600&height=400&seq=tygerberg-gallery-2&orientation=landscape',
      title: 'Medical Facility'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=modern%20office%20interior%20freshly%20painted%20neutral%20colors%20contemporary%20workspace%20clean%20simple%20background%20professional%20photography%20natural%20lighting&width=600&height=400&seq=tygerberg-gallery-3&orientation=landscape',
      title: 'Office Interior'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=retail%20store%20interior%20freshly%20painted%20modern%20design%20shopping%20space%20clean%20simple%20background%20professional%20photography%20bright%20natural%20light&width=600&height=400&seq=tygerberg-gallery-4&orientation=landscape',
      title: 'Retail Space'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=residential%20living%20room%20interior%20freshly%20painted%20warm%20colors%20comfortable%20furniture%20clean%20simple%20background%20professional%20photography%20natural%20lighting&width=600&height=400&seq=tygerberg-gallery-5&orientation=landscape',
      title: 'Living Room Interior'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=commercial%20building%20exterior%20freshly%20painted%20modern%20architecture%20business%20property%20clean%20simple%20background%20professional%20photography%20bright%20daylight&width=600&height=400&seq=tygerberg-gallery-6&orientation=landscape',
      title: 'Commercial Building'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-bold" style={{ color: '#e9f638' }}>
              Painters Directory
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-gray-300 transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-gray-300 transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-gray-300 transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="px-6 py-2 rounded-lg whitespace-nowrap transition-colors" style={{ backgroundColor: '#e9f638', color: '#000' }}>
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=tygerberg%20area%20cape%20town%20suburban%20neighborhood%20modern%20homes%20clean%20simple%20background%20professional%20photography%20bright%20natural%20lighting%20residential%20community&width=1920&height=600&seq=tygerberg-hero&orientation=landscape"
            alt="Tygerberg Painters"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div className="w-full">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Painters Tygerberg</h1>
            <p className="text-xl md:text-2xl text-white mb-6">Professional Painting Services for Homes &amp; Businesses</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/get-a-quote"
                className="px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
                style={{ backgroundColor: '#e9f638', color: '#000' }}
              >
                Get Free Quote
              </Link>
              <a
                href="tel:0219876543"
                className="px-8 py-3 bg-white text-black rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 sticky top-16 bg-white z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {['overview', 'services', 'gallery', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'overview' && (
          <div className="space-y-12">
            {/* About Section */}
            <section>
              <h2 className="text-3xl font-bold mb-6">About Painters Tygerberg</h2>
              <div className="prose max-w-none space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Welcome to Painters Tygerberg, your trusted partner for professional painting services in the Tygerberg area and surrounding Northern Suburbs of Cape Town. With over 18 years of experience in the painting industry, we have established ourselves as a leading painting contractor for residential homes, commercial properties, and medical facilities throughout the Tygerberg region and beyond.
                </p>
                <p>
                  The Tygerberg area is a vibrant and diverse community that includes residential neighborhoods, commercial districts, medical facilities, and educational institutions. Our team of expert painters understands the unique requirements of painting in this dynamic area, from suburban family homes to busy medical practices and commercial establishments. We take pride in delivering exceptional results that enhance the beauty and functionality of your property while providing long-lasting protection and value.
                </p>
                <p>
                  Our company was founded on the principles of quality craftsmanship, professional service, and customer satisfaction. Over the years, we have built a reputation for excellence by consistently delivering outstanding results on every project, whether it's a family home renovation, medical facility upgrade, office building refresh, or retail space transformation. We understand that your property is important to you, and we treat every project with the care, respect, and attention it deserves.
                </p>
                <p>
                  What sets Painters Tygerberg apart from other painting contractors is our versatility and expertise across different property types, our commitment to using quality paints and materials, and our team of highly skilled painters who are experienced in working with both residential and commercial properties. We work closely with homeowners, property managers, medical professionals, business owners, and facility managers to ensure that every aspect of the painting project meets or exceeds expectations while staying within budget and timeline.
                </p>
                <p>
                  Our expertise extends across all types of painting projects, including interior painting, exterior painting, commercial painting, medical facility painting, residential renovations, and maintenance programs. We are particularly experienced in working with medical facilities where hygiene, minimal disruption, and appropriate coatings are essential. Whether you're looking to refresh your family home, upgrade your medical practice, repaint your office building, or maintain a commercial property, we have the skills, experience, and resources to deliver exceptional results.
                </p>
                <p>
                  We understand that painting projects can be disruptive, especially in busy medical facilities and commercial environments. That's why we work efficiently and flexibly to minimize inconvenience while maintaining our high standards of quality. Our team is fully insured, professionally trained, and committed to maintaining a clean and safe work environment. We use dust-free sanding equipment, protective coverings, low-VOC paints where appropriate, and proper ventilation to ensure that your property remains clean, safe, and comfortable throughout the painting process.
                </p>
                <p>
                  At Painters Tygerberg, we believe in building long-term relationships with our clients based on trust, reliability, and exceptional service. Many of our clients are repeat customers who return to us for all their painting needs and recommend us to their friends, family, colleagues, and business associates. This word-of-mouth reputation is something we value highly and work hard to maintain with every project we undertake. We're proud to serve the Tygerberg community and contribute to making it a more beautiful place to live and work.
                </p>
                <p>
                  Our service area includes Tygerberg, Bellville, Parow, Goodwood, Brackenfell, Durbanville, Edgemead, and surrounding Northern Suburbs areas. We are familiar with the local architecture, property types, and community standards, which allows us to provide expert advice and recommendations tailored to your specific needs. Whether you own a family home, medical practice, office building, retail space, or commercial property, we have the expertise to deliver results that exceed your expectations while providing excellent value for your investment.
                </p>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-3xl font-bold mb-6">Why Choose Painters Tygerberg?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-medal-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">18+ Years Experience</h3>
                    <p className="text-gray-600">Extensive experience in residential, commercial, and medical facility painting throughout Tygerberg and Cape Town.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-hospital-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Medical Facility Specialists</h3>
                    <p className="text-gray-600">Specialized expertise in painting medical facilities with hygienic coatings and minimal disruption.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-team-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Professional Team</h3>
                    <p className="text-gray-600">Highly skilled painters with training in residential, commercial, and specialized facility painting.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Fully Insured</h3>
                    <p className="text-gray-600">Comprehensive insurance coverage for your peace of mind and property protection.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Flexible Scheduling</h3>
                    <p className="text-gray-600">We work around your schedule to minimize disruption to your home or business operations.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-price-tag-3-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Competitive Pricing</h3>
                    <p className="text-gray-600">Fair, transparent pricing with excellent value for quality workmanship and professional service.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Service Areas */}
            <section>
              <h2 className="text-3xl font-bold mb-6">Service Areas</h2>
              <p className="text-gray-700 mb-4">
                We proudly serve Tygerberg and surrounding Northern Suburbs areas including:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Tygerberg', 'Bellville', 'Parow', 'Goodwood', 'Brackenfell', 'Durbanville', 'Edgemead', 'Plattekloof'].map((area) => (
                  <div key={area} className="flex items-center space-x-2">
                    <i className="ri-map-pin-line" style={{ color: '#e9f638' }}></i>
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6">Our Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg mb-4" style={{ backgroundColor: '#e9f638' }}>
                      <i className={`${service.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Detailed Services */}
            <section className="prose max-w-none">
              <h2 className="text-3xl font-bold mb-6">Comprehensive Painting Solutions</h2>
              <div className="space-y-6 text-gray-700">
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Residential Painting Services</h3>
                  <p>
                    Our residential painting services are designed to transform your family home into a beautiful, comfortable living space. We offer comprehensive interior and exterior painting services using premium quality paints and expert techniques. From color consultation to final inspection, we work closely with you to ensure your home reflects your personal style and meets your expectations. Our residential services include whole house painting, room refreshes, feature walls, ceiling painting, trim and woodwork, and exterior weatherproofing. We understand that your home is your sanctuary, and we treat it with the respect and care it deserves.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Medical Facility Painting</h3>
                  <p>
                    Painting medical facilities requires specialized knowledge, appropriate materials, and careful planning to minimize disruption to patient care. Our medical facility painting services include hospitals, clinics, medical practices, dental offices, and healthcare centers. We use hygienic, antimicrobial coatings where appropriate, work flexible hours to accommodate patient schedules, and maintain strict cleanliness and safety protocols. We understand the importance of creating a clean, welcoming environment for patients while ensuring minimal disruption to medical operations. Our team is experienced in working within healthcare environments and follows all necessary protocols and regulations.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Commercial Property Painting</h3>
                  <p>
                    Your commercial property's appearance directly impacts your business image and customer perception. Our commercial painting services help you maintain a professional, attractive appearance that reflects your brand and values. We specialize in office buildings, retail spaces, shopping centers, restaurants, warehouses, and industrial facilities. Our commercial services include interior painting, exterior painting, maintenance programs, and emergency repairs. We work efficiently to minimize disruption to your business operations and can schedule work during off-hours or weekends if needed. Our team understands the importance of maintaining your business's professional appearance and works to deliver results that enhance your property's value and appeal.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Maintenance Painting Programs</h3>
                  <p>
                    Regular maintenance is essential to protect your property investment and maintain its appearance. Our maintenance painting programs are designed to keep your residential or commercial property looking its best year-round. We offer scheduled maintenance services, preventive inspections, touch-up painting, and comprehensive maintenance plans customized to your property's specific needs. Regular maintenance painting not only keeps your property looking beautiful but also helps identify and address potential issues before they become costly problems. Our maintenance programs provide peace of mind and help protect your property investment for years to come.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Work</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, index) => (
                <div key={index} className="group relative overflow-hidden rounded-lg aspect-video">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover object-top transition-transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <p className="text-white font-semibold text-lg">{image.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Customer Reviews</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((review, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{review.name}</h3>
                      <p className="text-sm text-gray-500">{review.date}</p>
                    </div>
                    <div className="flex space-x-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill" style={{ color: '#e9f638' }}></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: '#e9f638' }}>
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Property?</h2>
          <p className="text-xl mb-8">Get a free, no-obligation quote for your painting project today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/get-a-quote"
              className="px-8 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              Request Free Quote
            </Link>
            <a
              href="tel:0219876543"
              className="px-8 py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#e9f638' }}>Painters Tygerberg</h3>
              <p className="text-gray-400">Professional painting services for homes and businesses in Tygerberg and surrounding areas.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/get-a-quote" className="text-gray-400 hover:text-white transition-colors">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Residential Painting</li>
                <li>Medical Facilities</li>
                <li>Commercial Properties</li>
                <li>Maintenance Programs</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line"></i>
                  <span>021 987 6543</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Tygerberg, Cape Town</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Painters Tygerberg. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="https://leaderr.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Powered by leaderr
              </a>
              <a href="https://seostudio.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}